// i18n.ts — фасад подсистемы языков: t/useT — рендер по ключу, LocaleProvider — живая смена
// без F5, applyLocale — переключение словаря. Свой модуль (~120 строк по канону), без
// библиотек: ровно то, что используется от i18next/react-intl — t() и смена языка.
import { Fragment, createElement, useEffect, useSyncExternalStore } from "react";
import { typograph, setKitLocale } from "@kit-v5";
import { availableLocales, isLocale, loadDict } from "./dict";
import { selectPlural } from "./plural";
import { interpolate } from "./format";
import { sharedEventBus } from "../osnovaContext";
import { loadAllPrefs } from "../lib/userPrefs";
import { useAuthStore } from "../state/authStore";
const DEFAULT_LOCALE = "ru";
const STORAGE_KEY = "osnova-locale";
let activeLocale = resolveBootLocale();
let activeDict = {};
let fallbackDict = {};
// revision — растёт при каждой подмене словаря; useT подписан на неё, не на локаль:
// locale может не меняться (тот же 'ru'), а словарь при этом приходит позже первого кадра
// (асинхронный чанк) — снимок-строка не изменится, и React не перерисует читателей t().
let revision = 0;
const listeners = new Set();
// resolveBootLocale — первый кадр красится синхронным атрибутом документа (public/index.html
// выставляет его из localStorage до отрисовки — тот же приём, что уже есть у темы).
function resolveBootLocale() {
    const attr = document.documentElement.dataset.locale;
    if (isLocale(attr))
        return attr;
    try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (isLocale(cached))
            return cached;
    }
    catch {
        /* хранилище недоступно (приватный режим, квота) */
    }
    return DEFAULT_LOCALE;
}
function notify() {
    listeners.forEach((cb) => cb());
}
export function getLocale() {
    return activeLocale;
}
export function getRevision() {
    return revision;
}
// getDictReady — снимок для гейта отрисовки: стартовый словарь ещё не загружен (пустой) —
// значит t() будет отдавать голые ключи, показывать компонент рано.
export function getDictReady() {
    return Object.keys(activeDict).length > 0;
}
export function subscribeLocale(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}
// applyLocale — грузит целевой словарь и русский фолбэк (И-6), делает активным, кеширует
// выбор. Загрузка — ДО смены состояния: иначе между сменой и приходом чанка мелькнут ключи.
export async function applyLocale(locale) {
    if (!isLocale(locale))
        throw new Error(`i18n: неизвестная локаль "${locale}"`);
    const [dict, fb] = await Promise.all([loadDict(locale), loadDict(DEFAULT_LOCALE)]);
    activeLocale = locale;
    activeDict = dict;
    fallbackDict = fb;
    document.documentElement.lang = locale;
    document.documentElement.dataset.locale = locale;
    try {
        localStorage.setItem(STORAGE_KEY, locale);
    }
    catch {
        /* приватный режим/квота — язык просто не переживёт перезагрузку */
    }
    // Кит — синглтон на весь документ (import map, см. webpack.config.js); язык платформы
    // известен только здесь, поэтому детали кита (кнопки, календари, пустые состояния) переключает Оболочка.
    setKitLocale(locale);
    revision += 1;
    notify();
}
// pickServerLocale — чистый разбор ответа /api/prefs (вынесена из LocaleProvider ради теста без
// React-рендера, см. i18n.test.ts); нет ключа/значение незнакомое — undefined, язык не меняется.
export function pickServerLocale(prefs) {
    const raw = prefs.locale;
    return isLocale(raw) ? raw : undefined;
}
// applyServerLocale — источник истины после входа: общий кеш lib/userPrefs.ts (тот же запрос,
// что уже делает useTheme/resolveTheme — второго GET /api/prefs не возникает).
export async function applyServerLocale() {
    const prefs = await loadAllPrefs();
    const serverLocale = pickServerLocale(prefs);
    if (serverLocale && serverLocale !== activeLocale)
        await applyLocale(serverLocale);
}
// t — рендер строки по ключу; вне компонента (адаптеры вроде loginApi.ts) читает тот же
// модульный активный словарь, что видит React-дерево — расхождений нет.
export function t(key, args) {
    const own = activeDict[key];
    const fallback = fallbackDict[key];
    const raw = own ?? fallback;
    if (raw === undefined) {
        if (process.env.NODE_ENV !== "production")
            console.warn(`i18n: ключ "${key}" не найден`);
        return key;
    }
    if (own === undefined && process.env.NODE_ENV !== "production") {
        console.warn(`i18n: ключ "${key}" отсутствует в "${activeLocale}", показан ru`);
    }
    const count = typeof args?.count === "number" ? args.count : Number(args?.count ?? 0);
    const template = typeof raw === "string" ? raw : selectPlural(activeLocale, count, raw);
    const rendered = interpolate(template, args);
    // Русская типографика — только на ru: на английском тексте те же правила портят числа/единицы.
    return activeLocale === "ru" ? typograph(rendered) : rendered;
}
// useT — привязывает t к текущей ревизии словаря: смена языка перерисовывает компонент.
export function useT() {
    useSyncExternalStore(subscribeLocale, getRevision);
    return t;
}
// useDictReady — true после того, как стартовый словарь встал в activeDict (см. applyLocale).
// Гейт для рендера: до этого t() отдаёт голые ключи вместо текста.
export function useDictReady() {
    return useSyncExternalStore(subscribeLocale, getDictReady);
}
// LocaleProvider — грузит стартовый словарь и слушает живую смену (PUT /api/prefs → prefsBridge
// → sharedEventBus, включая другие вкладки через storage — путь общий, см. lib/prefsBridge.ts).
// Состояние живёт в модульном сторе выше, компонент только подключает эффекты.
export function LocaleProvider({ children }) {
    const userId = useAuthStore((s) => s.user?.userId);
    useEffect(() => {
        if (Object.keys(activeDict).length > 0)
            return;
        applyLocale(activeLocale).catch((e) => console.error("i18n: не удалось загрузить стартовый словарь", e));
    }, []);
    // Источник истины — личные настройки на сервере: localStorage выше — только мгновенный старт
    // до ответа; до входа userId нет, запрос не идёт (страница логина, экран активации).
    useEffect(() => {
        if (!userId)
            return;
        applyServerLocale().catch((e) => console.error("i18n: не удалось применить язык из личных настроек", e));
    }, [userId]);
    useEffect(() => {
        return sharedEventBus.on("platform:prefs", (data) => {
            const change = data;
            if (change?.key !== "locale" || !isLocale(change.value))
                return;
            applyLocale(change.value).catch((e) => {
                // Провал загрузки чанка — язык не меняется молча, ошибка в консоль (§4.5).
                console.error(`i18n: не удалось переключить язык на "${change.value}"`, e);
            });
        });
    }, []);
    return createElement(Fragment, null, children);
}
export { availableLocales };
